import { MapPin, Video } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils.ts";
import { PickerSearchInput } from "./picker-search-input.tsx";
import type { LocationEntry } from "./types.ts";
import { useAutoFocusRef } from "./usePickerKeyboard.ts";

interface LocationPickerProps {
  onSelect: (location: LocationEntry) => void;
  /** Called when the highlighted option changes (arrow keys / hover) so the parent can preview connector words. */
  onPreview?: (location: LocationEntry) => void;
  /** Previously selected value — pre-selects the matching card and pre-fills the address input. */
  defaultValue?: LocationEntry | null;
}

/** Two-card location picker: Virtual or In-person (with address input). */
export function LocationPicker({ onSelect, onPreview, defaultValue }: LocationPickerProps) {
  const rootRef = useAutoFocusRef<HTMLDivElement>();
  const [showAddressInput, setShowAddressInput] = useState(defaultValue?.type === "in-person");
  const [address, setAddress] = useState(defaultValue?.details ?? "");
  const [selectedIndex, setSelectedIndex] = useState(defaultValue?.type === "in-person" ? 1 : 0);

  // Notify parent of the highlighted option for real-time connector word preview.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire on selectedIndex or address-mode change
  useEffect(() => {
    if (!showAddressInput) {
      onPreview?.(selectedIndex === 0 ? { type: "virtual" } : { type: "in-person" });
    }
  }, [selectedIndex, showAddressInput]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // When address input is focused, only handle Enter/Tab for submission
      // (let empty Tab bubble through so the dropdown's handler can skip the step)
      if (showAddressInput) return;

      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev === 0 ? 1 : 0));
      } else if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        e.stopPropagation();
        if (selectedIndex === 0) {
          onSelect({ type: "virtual" });
        } else {
          setShowAddressInput(true);
          onSelect({ type: "in-person" });
        }
      }
    },
    [showAddressInput, selectedIndex, onSelect],
  );

  const handleAddressSubmit = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        if (address.trim()) {
          e.preventDefault();
          e.stopPropagation();
          onSelect({ type: "in-person", details: address.trim() });
        }
        // Empty address: don't preventDefault — let Tab bubble to the
        // dropdown shell's handler so it can skip to the next step.
      }
    },
    [address, onSelect],
  );

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className="flex flex-col gap-2 p-3 outline-none"
      onKeyDown={handleKeyDown}
    >
      <div className="flex gap-2">
        {/* Virtual */}
        <button
          type="button"
          className={cn(
            "flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border cursor-pointer",
            !showAddressInput && selectedIndex === 0
              ? "border-secondary bg-secondary"
              : "hover:border-secondary",
          )}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect({ type: "virtual" });
          }}
          onMouseEnter={() => setSelectedIndex(0)}
        >
          <Video className="size-5 text-foreground" />
          <span className="text-sm font-medium">Virtual</span>
        </button>

        {/* In-person */}
        <button
          type="button"
          className={cn(
            "flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border cursor-pointer",
            showAddressInput || (!showAddressInput && selectedIndex === 1)
              ? "border-secondary bg-secondary"
              : "hover:border-secondary",
          )}
          onMouseDown={(e) => {
            e.preventDefault();
            setShowAddressInput(true);
            onSelect({ type: "in-person" });
          }}
          onMouseEnter={() => setSelectedIndex(1)}
        >
          <MapPin className="size-5 text-foreground" />
          <span className="text-sm font-medium">In-person</span>
        </button>
      </div>

      {showAddressInput && (
        <PickerSearchInput
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={handleAddressSubmit}
          onClear={() => setAddress("")}
          placeholder="Enter location or address..."
          autoFocus
        />
      )}
    </div>
  );
}
